import {usePathname} from '@aboutyou/router';
import {FIRST_PRODUCT_ID_PARAM} from '../../../features/firstProductId/useFirstProductIds';
import {useMergedPath} from '../../../shared-deprecated/useMergedPath';

const useStoryPath = () => {
  const pathname = usePathname();

  return (categoryId?: number): string => {
    if (!categoryId) {
      return pathname;
    }

    return `${pathname}?category=${categoryId}`;
  };
};

// Preserves the current filters/sort while switching category. Safe to use whenever
// the target category is known to have products under the current filters (the
// backend only ever tells us that via `productCount`/`hasProducts` on nodes that
// were reachable in the first place), unlike `useStoryPath`'s clean-reset variant.
export const useStoryFilteredCategoryPath = () => {
  const storyPath = useStoryPath();
  const getMergedPath = useMergedPath();

  return (categoryId?: number): string =>
    getMergedPath({
      path: storyPath(categoryId),
      config: {
        excludeUrlParams: new Set(['category', FIRST_PRODUCT_ID_PARAM]),
      },
    });
};

export const useCategoryLinkPaths = () => {
  const storyPath = useStoryPath();
  const storyFilteredCategoryPath = useStoryFilteredCategoryPath();

  return (
    categoryId?: number,
    // Pass true for a category known to have 0 products under the current
    // filters - the filter combination that produced that isn't meaningful
    // there, so the link should reset filters instead of carrying them over.
    {resetFilters = false}: {resetFilters?: boolean} = {},
  ) => {
    const cleanPath = storyPath(categoryId);

    if (resetFilters) {
      // `targetPath` is left unset rather than set to `cleanPath`: consumers
      // (e.g. NavigationTree) merge current URL params back onto whatever
      // `targetPath` they're given, which would silently re-add the filters
      // this is supposed to clear.
      return {path: cleanPath, targetPath: undefined};
    }

    return {
      path: storyFilteredCategoryPath(categoryId),
      targetPath: cleanPath,
    };
  };
};
